import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { CreditCard, Send, Loader2 } from "lucide-react"; // 👈 Add Loader2 here
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import ProtectDashboardRoute from "../components/ProtectDashboardRoute";
import config from "../config";
import MobileHeader from "../components/MobileHeader";

export default function AddMoneyPage() {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("card");
  const [showModal, setShowModal] = useState(false);
  const [profile, setProfile] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // Loading state

  // Quick amount options
  const quickAmounts = [1000, 2000, 5000, 10000];

  // 🔹 Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        console.error("No token found");
        navigate("/login");
        return;
      }

      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);

        // Handle specific error cases
        if (err.response?.status === 401) {
          console.log("Token invalid or expired");
          localStorage.removeItem("authToken");
          navigate("/login");
        } else if (err.response?.status === 403) {
          console.log("Access forbidden");
        } else {
          console.log("Network or server error");
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
  };

  const handlePayment = async () => {
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount!");
      return;
    }

    if (amount < 500) {
      toast.error("Minimum deposit amount is ₦500!");
      return;
    }

    // Check if user agreed to terms
    if (!agreedToTerms) {
      toast.error("Please confirm that you understand the payment process");
      return;
    }

    // Optional: Check for maximum limit
    if (amount > 1000000) {
      toast.error("Maximum deposit amount is ₦1,000,000!");
      return;
    }

    if (!profile?._id || !profile?.email) {
      toast.error("User not loaded. Please log in again.");
      return;
    }

    if (method === "card") {
      try {
        setIsLoading(true); // 👈 Start loading

        // 🔥 Call backend to initialize Squad payment
        const res = await axios.post(
          `${config.API_BASE_URL}/api/payment/initialize`,
          {
            amount: parseInt(amount),
            email: profile.email,
            userId: profile._id,
          },
        );

        const { authorization_url } = res.data.data;

        toast.success("Redirecting to Squad...");
        window.location.href = authorization_url;
        // No need to set isLoading false here because we're redirecting
      } catch (err) {
        console.error("Payment init error:", err);
        toast.error(err.response?.data?.error || "Unable to start payment");
        setIsLoading(false); // 👈 Stop loading on error
      }
    } else {
      // Transfer method → show modal with bank details
      setShowModal(true);
      toast("Follow the transfer instructions", { icon: "💸" });
    }
  };

  const formatNumber = (num) => {
    if (!num) return "";
    // Remove anything that is not a digit
    const numeric = num.toString().replace(/[^0-9]/g, ""); // 👈 Removed dot to only allow whole numbers
    // Format with commas
    return Number(numeric).toLocaleString();
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers (no dots)
    const numericValue = value.replace(/[^0-9]/g, ""); // 👈 Removed dot to only allow whole numbers
    setAmount(numericValue);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <ProtectDashboardRoute />
      <Toaster position="top-center" />
      {/* Sidebar */}
      <MobileHeader />
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col mt-[60px]">
        <main className="p-5 flex-1">
          <div className="bg-white rounded-2xl shadow p-8 max-w-lg mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Fund Wallet
            </h2>

            <p className="text-gray-600 mb-6">
              Enter the amount you want to deposit.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Note:</span> Minimum deposit
                amount is
                <span className="font-bold"> ₦500.00</span>
              </p>
            </div>

            {/* Quick Amount Buttons */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-3">
                Quick Amount
              </label>
              <div className="grid grid-cols-2 gap-3">
                {quickAmounts.map((quickAmount) => (
                  <button
                    key={quickAmount}
                    onClick={() => handleQuickAmount(quickAmount)}
                    className={`py-3 px-4 rounded-lg border-2 transition-all font-medium
                      ${
                        amount === quickAmount.toString()
                          ? "border-[#007AFF] bg-blue-50 text-[#007AFF]"
                          : "border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                  >
                    ₦{quickAmount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Enter Amount (₦)
              </label>
              <input
                type="tel" // 👈 Changed from "text" to "tel" for numeric keyboard
                placeholder="e.g. 2000"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formatNumber(amount)}
                onChange={handleAmountChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#007AFF] outline-none"
              />
            </div>

            {/* Terms Checkbox */}
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-[#007AFF] border-gray-300 rounded focus:ring-[#007AFF]"
                />
                <span className="text-sm text-gray-600">
                  I understand that my payment requires a brief confirmation
                  period, and I agree to wait to be redirected back to my
                  dashboard.
                </span>
              </label>
            </div>

            {/* Action Button */}
            <button
              onClick={handlePayment}
              disabled={!agreedToTerms || isLoading}
              className={`w-full flex items-center justify-center gap-2 text-white px-6 py-3 rounded-lg shadow transition text-lg font-semibold
                ${
                  agreedToTerms && !isLoading
                    ? "bg-[#007AFF] hover:bg-[#005FCC] cursor-pointer"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : method === "card" ? (
                <>
                  <CreditCard className="w-5 h-5" /> Fund Wallet
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" /> Get Transfer Details
                </>
              )}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
